import React, { useEffect, useRef, useState } from "react";
import { Download, Save, Upload } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { EaSeg, inputCls } from "./EaUi";
import { MONTHS_IT, gasLabel } from "./eaFormat";

const SRC = [["self", "Autoproduzione rinnovabile"], ["ppa_onsite", "PPA on-site"], ["ppa_offsite", "PPA off-site"], ["biomethane", "Biometano certificato"]];
const HINT = {
  self: "Energia prodotta da impianti rinnovabili propri e consumata in sito (non passa dal contatore del POD): si somma ai consumi.",
  ppa_onsite: "Energia da impianto di terzi installato in sito con PPA, consumata in sito: si somma ai consumi. Indica il prezzo PPA in €/MWh per calcolare il risparmio netto.",
  ppa_offsite: "Energia rinnovabile acquistata con PPA da impianto esterno: copre parte dei prelievi di rete.",
  biomethane: "Quota di gas consumato coperta da biometano certificato (Garanzie di Origine biometano).",
};
const M12 = Array.from({ length: 12 }, (_, i) => i + 1);
const str = (v) => (v == null ? "" : String(v).replace(".", ","));
export const toNum = (t) => {
  const s = String(t ?? "").trim();
  if (!s) return null;
  const n = Number(s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s);
  return Number.isNaN(n) || n < 0 ? NaN : n;
};

function fromDocs(green) {
  const st = {};
  green.forEach((g) => {
    st[`${g.source}|${g.code || ""}`] = { annual: str(g.annual_pct ?? g.annual), pct: g.annual_pct != null, price: str(g.price_eur_mwh), months: Object.fromEntries(Object.entries(g.months || {}).map(([m, v]) => [m, str(v)])) };
  });
  return st;
}

function GreenRow({ k, label, sub, row, gas, ppa, onChange }) {
  const r = row || { annual: "", pct: false, price: "", months: {} };
  const set = (patch) => onChange(k, { ...r, ...patch });
  const id = (k.endsWith("|") ? `${k}AZIENDA` : k).replace(/[^A-Za-z0-9]+/g, "-");
  return (
    <tr className="border-t border-slate-100" data-testid={`sus-green-row-${id}`}>
      <td className="px-2 py-1.5 min-w-[180px] text-xs"><b className="text-slate-800">{label}</b>{sub && <span className="block text-slate-400">{sub}</span>}</td>
      <td className="px-1 py-1.5 whitespace-nowrap">
        <input inputMode="decimal" value={r.annual} onChange={(e) => set({ annual: e.target.value })} className={`${inputCls} w-24 text-right`} data-testid={`sus-green-annual-${id}`} />
        {gas && <select value={r.pct ? "pct" : "smc"} onChange={(e) => set({ pct: e.target.value === "pct" })} className={`${inputCls} ml-1 w-16`} data-testid={`sus-green-mode-${id}`}><option value="smc">Smc</option><option value="pct">%</option></select>}
      </td>
      {ppa && <td className="px-1 py-1.5"><input inputMode="decimal" value={r.price ?? ""} onChange={(e) => set({ price: e.target.value })} placeholder="€/MWh" className={`${inputCls} w-20 text-right`} data-testid={`sus-green-price-${id}`} /></td>}
      {M12.map((m) => (
        <td key={m} className="px-0.5 py-1.5"><input inputMode="decimal" value={r.months[m] ?? ""} onChange={(e) => set({ months: { ...r.months, [m]: e.target.value } })} className={`${inputCls} w-16 text-right px-1`} data-testid={`sus-green-m${m}-${id}`} /></td>
      ))}
    </tr>
  );
}

function GreenExcel({ year, params, onData }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const template = async () => {
    try {
      const r = await api.get("/ea/sustainability/green-template.xlsx", { params: { ...params, year }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      Object.assign(document.createElement("a"), { href: url, download: `fonti_rinnovabili_${year}.xlsx` }).click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Modello non disponibile"); }
  };
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/ea/sustainability/green-upload", fd, { params: { ...params, year } });
      onData(r.data);
      toast.success(`Importate ${r.data.imported_rows} righe dal file Excel`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Importazione non riuscita");
    }
    setBusy(false);
  };
  const btn = "h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm inline-flex items-center gap-2 disabled:opacity-60";
  return (
    <div className="flex flex-wrap gap-2">
      <button onClick={template} className={btn} data-testid="sus-green-template"><Download size={15} /> Scarica modello Excel</button>
      <button onClick={() => ref.current?.click()} disabled={busy} className={btn} data-testid="sus-green-upload"><Upload size={15} /> {busy ? "Importazione…" : "Importa Excel"}</button>
      <input ref={ref} type="file" accept=".xlsx" className="hidden" onChange={upload} data-testid="sus-green-upload-input" />
    </div>
  );
}

export function GreenForm({ data, points, save, params, onData }) {
  const [src, setSrc] = useState("self");
  const [st, setSt] = useState(() => fromDocs(data.green));
  useEffect(() => setSt(fromDocs(data.green)), [data]);
  const gas = src === "biomethane";
  const pts = points.filter((p) => p.commodity === (gas ? "GAS" : "POWER"));
  const onChange = (k, v) => setSt({ ...st, [k]: v });
  const submit = () => {
    const items = [];
    for (const [k, r] of Object.entries(st)) {
      const [source, code] = k.split("|");
      const a = toNum(r.annual), price = source === "ppa_onsite" ? toNum(r.price) : null;
      const months = Object.fromEntries(Object.entries(r.months).map(([m, v]) => [m, toNum(v)]));
      if (Number.isNaN(a) || Number.isNaN(price) || Object.values(months).some(Number.isNaN) || (r.pct && a > 100)) return toast.error("Inserisci numeri ≥ 0 (percentuale massima 100)");
      items.push({ code: code || null, source, annual: r.pct ? null : a, annual_pct: r.pct ? a : null, price_eur_mwh: price, months });
    }
    save("put", "/ea/sustainability/green", { year: data.year, items }, "Fonti rinnovabili salvate");
  };
  return (
    <div className="card-elevated p-5" data-testid="sus-green-form">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="font-display font-semibold text-slate-900">Autoproduzione, PPA e biometano {data.year}</div>
        <GreenExcel year={data.year} params={params} onData={onData} />
      </div>
      <div className="text-xs text-slate-500 mb-3">Inserisci per singolo punto di fornitura il totale annuo oppure i valori mensili ({gas ? "Smc" : "MWh"}). Se compili i mesi, il totale è la loro somma; con il solo dato annuo autoproduzione e PPA on-site vengono ripartiti con il profilo fotovoltaico tipico, PPA off-site e biometano in proporzione ai consumi.</div>
      <EaSeg value={src} onChange={setSrc} options={SRC} testid="sus-green-source" />
      <div className="text-xs text-emerald-700 mt-2" data-testid="sus-green-hint">{HINT[src]}</div>
      <div className="overflow-x-auto mt-3">
        <table className="text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="px-2 py-1 text-left">Punto</th><th className="px-1 py-1 text-left">Annuale</th>{src === "ppa_onsite" && <th className="px-1 py-1 text-left">Prezzo PPA</th>}{M12.map((m) => <th key={m} className="px-0.5 py-1">{MONTHS_IT[m - 1]}</th>)}</tr></thead>
          <tbody>
            {pts.map((p) => <GreenRow key={p.code} k={`${src}|${p.code}`} label={gas ? gasLabel(p) : `POD ${p.identifier}`} sub={[p.label, p.address].filter(Boolean).join(" · ")} row={st[`${src}|${p.code}`]} gas={gas} ppa={src === "ppa_onsite"} onChange={onChange} />)}
            <GreenRow k={`${src}|`} label="Non attribuito a un punto" sub="Conteggiato solo nel totale aziendale" row={st[`${src}|`]} gas={gas} ppa={src === "ppa_onsite"} onChange={onChange} />
          </tbody>
        </table>
      </div>
      <button onClick={submit} className="mt-3 h-9 px-4 rounded-lg bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2" data-testid="sus-green-save"><Save size={15} /> Salva fonti rinnovabili</button>
      <div className="text-xs text-slate-400 mt-1">Il salvataggio comprende tutte le fonti. Lascia vuoti i campi per rimuovere un dato. Con l'import Excel vengono aggiornate solo le righe compilate nel file.</div>
    </div>
  );
}
